import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Send } from 'lucide-react'
import Modal from '../ui/Modal'
import { todayAsInputDate } from '../../lib/datetime'

/**
 * Confirmación de emisión con FECHA elegible.
 *
 * Antes se emitía siempre con la fecha del día: si el trabajo era de julio y lo
 * facturabas en agosto, la factura salía de agosto y se iba al trimestre que no
 * era. El backend ya aceptaba `issueDate` en /invoices/:id/emit; lo que faltaba
 * era poder decírselo.
 *
 * Emitir es irreversible (congela número, fecha, hash, CSV y asiento), así que
 * el diálogo dice en qué se está metiendo el usuario antes de pulsar.
 */
export default function EmitInvoiceModal({
  open,
  onClose,
  onConfirm,
  busy,
  summary,
}: {
  open: boolean
  onClose: () => void
  /** Recibe la fecha elegida en formato YYYY-MM-DD. */
  onConfirm: (issueDate: string) => void
  busy?: boolean
  /** Qué se va a emitir (cliente, concepto e importe), cuando la factura aún no existe (Pendientes). */
  summary?: string
}) {
  const { t } = useTranslation('billing')
  // Hoy en la zona del usuario. Con `toISOString()` sería el día UTC, que entre
  // medianoche y las 02:00 de Madrid es AYER: el tope del selector se quedaba
  // corto y no dejaba fechar la factura en el día en que se está emitiendo.
  const today = todayAsInputDate()
  const [issueDate, setIssueDate] = useState(today)

  // Al reabrir vuelve a hoy: no arrastrar la fecha de una emisión anterior.
  useEffect(() => {
    if (open) setIssueDate(today)
  }, [open, today])

  const isPast = issueDate < today
  // ☠️ El `max` del selector solo vale para el calendario: una fecha futura escrita a mano pasaba, se creaba el borrador (Pendientes) y
  // /emit la rechazaba con un 400 (una factura con fecha posterior a hoy no existe todavía y descuadra el 303 del trimestre).
  const isFuture = !!issueDate && issueDate > today
  const changesYear = issueDate.slice(0, 4) !== today.slice(0, 4)

  return (
    <Modal open={open} onClose={onClose} title={t('emitModal.title')} maxWidth="max-w-md">
      <div className="space-y-4">
        {summary && <p className="text-sm font-semibold t-primary">{summary}</p>}
        <p className="text-sm t-secondary">{t('emitModal.intro')}</p>

        <div>
          <label className="block text-xs t-muted mb-1">{t('emitModal.issueDate')}</label>
          <input
            type="date"
            value={issueDate}
            max={today}
            onChange={(e) => setIssueDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm surface-soft border border-base t-primary"
          />
          <p className="text-xs t-muted mt-1">{t('emitModal.issueDateHint')}</p>
        </div>

        {isFuture && (
          <div
            className="flex items-start gap-2 text-xs rounded-lg p-2.5 text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30"
            data-testid="emit-future-date"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t('emitModal.futureDate')}</span>
          </div>
        )}

        {isPast && (
          <div className="flex items-start gap-2 text-xs rounded-lg p-2.5 text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              {changesYear ? t('emitModal.warnOtherYear') : t('emitModal.warnBackdated')}
            </span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(issueDate)}
            disabled={busy || !issueDate || isFuture}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t('emitModal.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
